import { useMemo } from "react"
import type { DTADocumentContent } from "../../../types/dta"

/**
 * Represents a field extracted from the Metadata tab's transfer_metadata_schema.
 */
export interface MetadataField {
  /**
   * The variable name (used as the field identifier in rules)
   */
  name: string

  /**
   * The human-readable label for the field (shown in tooltips)
   */
  label?: string
}

interface MetadataSchema {
  variables?: Array<{
    variable_name?: string
    variable_label?: string
  }>
}

/**
 * Hook to extract field names and labels from the Metadata tab's transfer_metadata_schema. Filters
 * to variables that have a variable_name defined.
 *
 * @param content - The DTA document content containing transfer_metadata_schema.
 * @returns Array of MetadataField objects with name and optional label
 */
export function useMetadataFields(content: DTADocumentContent): MetadataField[] {
  return useMemo(() => {
    const schema = content.transfer_metadata_schema as MetadataSchema | undefined
    const variables = schema?.variables || []

    return variables
      .filter((v): v is { variable_name: string; variable_label?: string } => !!v.variable_name)
      .map((v) => ({ name: v.variable_name, label: v.variable_label }))
  }, [content.transfer_metadata_schema])
}
